// Auth helpers for AD GALAXY.
// Synthetic email pattern: username@adgalaxy.local
// No email is ever shown in the UI.

import { getSupabase, resetSupabaseClient } from './supabase';

export interface Profile {
  id: string;
  username: string;
  galaxy_name: string;
  role: 'admin' | 'member';
  show_money: boolean;
  disabled: boolean;
  must_change_password: boolean;
  model: string;
  created_at: string;
}

function syntheticEmail(username: string): string {
  return `${username.toLowerCase().trim()}@adgalaxy.local`;
}

// ─────────────────────────────────────────────────────────────────────────────
// PROOF 4 — Auth response codes (failed + successful sign-in)
// ─────────────────────────────────────────────────────────────────────────────
// Sign in with username + password.
// Constructs synthetic email internally -- never exposed in UI.
export async function signIn(username: string, password: string) {
  const supabase = getSupabase();
  const email = syntheticEmail(username);

  const result = await supabase.auth.signInWithPassword({ email, password });

  // ── PROOF 4: Auth response codes ──────────────────────────────────────────
  console.group('▶ PROOF 4 — Auth response codes');
  if (result.error) {
    console.log('PROOF 4 [FAILED sign-in]  HTTP status :', result.error.status);
    console.log('PROOF 4 [FAILED sign-in]  error.message:', result.error.message);
    console.log('PROOF 4 [FAILED sign-in]  error.code   :', (result.error as any).code ?? 'n/a');
  } else {
    console.log('PROOF 4 [SUCCESS sign-in] HTTP status  : 200 (no error object)');
    console.log('PROOF 4 [SUCCESS sign-in] user.id      :', result.data?.user?.id);
    console.log('PROOF 4 [SUCCESS sign-in] user.email   :', result.data?.user?.email);
  }
  console.groupEnd();

  return result;
}

// Sign out current user.
export async function signOut() {
  const supabase = getSupabase();
  const result = await supabase.auth.signOut();
  resetSupabaseClient(); // no-op on singleton, kept for compat
  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// PROOF 1 — Forced password change flow
// ─────────────────────────────────────────────────────────────────────────────
// Update password for the currently signed-in user.
// Also sets must_change_password = false on the profile.
export async function updatePassword(newPassword: string) {
  const supabase = getSupabase();

  // ── PROOF 1: updateUser response ──────────────────────────────────────────
  console.group('▶ PROOF 1 — Forced password change flow');
  console.log('PROOF 1 [updatePassword] calling supabase.auth.updateUser...');

  const updateResult = await supabase.auth.updateUser({ password: newPassword });

  if (updateResult.error) {
    console.log('PROOF 1 [updatePassword] FAILED  status :', updateResult.error.status);
    console.log('PROOF 1 [updatePassword] FAILED  message:', updateResult.error.message);
  } else {
    console.log('PROOF 1 [updatePassword] SUCCESS — no error returned');
    console.log('PROOF 1 [updatePassword] updated user.id:', updateResult.data?.user?.id);
  }

  if (updateResult.error) {
    console.groupEnd();
    return updateResult;
  }

  // Clear the must_change_password flag
  const { data: { user } } = await supabase.auth.getUser();
  if (user) {
    const { error: profileErr } = await supabase
      .from('profiles')
      .update({ must_change_password: false })
      .eq('id', user.id);

    console.log(
      'PROOF 1 [must_change_password → false]',
      profileErr ? `FAILED: ${profileErr.message}` : 'SUCCESS — flag cleared'
    );
  }
  console.groupEnd();

  return updateResult;
}

// Fetch the profile for the currently signed-in user.
// Uses the shared singleton — supabase-js attaches the JWT automatically.
// Accepts an optional userId to skip the getUser() round-trip when the
// caller already has the id (e.g. immediately after signInWithPassword).
export async function getOwnProfile(userId?: string): Promise<Profile | null> {
  const supabase = getSupabase();

  // DIAGNOSTIC: confirm the singleton has a live session before the SELECT.
  const { data: { session: diagSession } } = await supabase.auth.getSession();
  console.log(
    'AUTH DIAG: getSession before profiles SELECT —',
    diagSession ? `session exists, user id = ${diagSession.user.id}` : 'NO SESSION (auth.uid() will be null under RLS)'
  );

  let uid = userId;
  if (!uid) {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) {
      console.error('AUTH: getUser error', userError.message);
      return null;
    }
    if (!user) return null;
    uid = user.id;
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', uid)
    .single();

  if (error) {
    console.error('AUTH: profiles fetch error', error.message, 'code:', error.code, 'uid:', uid);
    return null;
  }
  if (!data) {
    console.error('AUTH: profiles fetch returned no data for uid', uid);
    return null;
  }

  const profile = data as Profile;
  console.log('AUTH PROOF 2: user role =', profile.role, '| isAdmin =', profile.role === 'admin');

  return profile;
}

// Update galaxy_name for the current user.
export async function updateGalaxyName(galaxyName: string) {
  const supabase = getSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: { message: 'Not signed in' } };

  return supabase
    .from('profiles')
    .update({ galaxy_name: galaxyName.toUpperCase() })
    .eq('id', user.id);
}

// Update model preference for the current user.
export async function updateModel(model: string) {
  const supabase = getSupabase();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: { message: 'Not signed in' } };

  return supabase
    .from('profiles')
    .update({ model })
    .eq('id', user.id);
}

// Fetch all profiles (admin only -- RLS enforces this).
export async function getAllProfiles(): Promise<Profile[]> {
  const supabase = getSupabase();

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('username');

  console.log('AUTH PROOF 3: profiles query result', data ?? error?.message);

  if (error || !data) return [];
  return data as Profile[];
}

// Fetch org_policies singleton.
export async function getOrgPolicies() {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('org_policies')
    .select('*')
    .single();

  if (error || !data) return null;
  return data as {
    id: number;
    allowed_models: string[];
    default_model: string;
    monthly_spend_cap_usd: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PROOF 3a — RLS rejection: pilot tries to update visma's galaxy_name
// Expected: 0 rows affected (RLS silently blocks cross-user UPDATE)
// ─────────────────────────────────────────────────────────────────────────────
export async function rlsProofUpdateOtherGalaxy(targetUsername: string, newName: string) {
  const supabase = getSupabase();

  console.group('▶ PROOF 3a — RLS: update another user\'s galaxy_name');
  console.log('PROOF 3a attempting to update galaxy_name of username:', targetUsername, '→', newName);

  const { data, error, count } = await supabase
    .from('profiles')
    .update({ galaxy_name: newName })
    .eq('username', targetUsername)
    .select();

  const rowsAffected = count ?? data?.length ?? 0;
  console.log('PROOF 3a rows affected :', rowsAffected, '(expected: 0 — RLS blocks cross-user write)');
  console.log('PROOF 3a error         :', error?.message ?? 'none (RLS silently returns 0 rows)');
  console.log('PROOF 3a VERDICT       :', rowsAffected === 0 ? '✅ PASS — RLS rejected the update' : '❌ FAIL — update went through');
  console.groupEnd();

  return { data, error, rowsAffected };
}

// ─────────────────────────────────────────────────────────────────────────────
// PROOF 3b — RLS rejection: pilot tries to set own role to 'admin'
// Expected: trigger raises exception "Only admins can change role"
// ─────────────────────────────────────────────────────────────────────────────
export async function rlsProofEscalateOwnRole() {
  const supabase = getSupabase();

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.warn('PROOF 3b: no signed-in user — skipping');
    return { data: null, error: { message: 'Not signed in' } };
  }

  console.group('▶ PROOF 3b — Trigger: member tries to set own role to admin');
  console.log('PROOF 3b current user id:', user.id);
  console.log('PROOF 3b attempting UPDATE profiles SET role = \'admin\' WHERE id =', user.id);

  const { data, error } = await supabase
    .from('profiles')
    .update({ role: 'admin' })
    .eq('id', user.id)
    .select();

  if (error) {
    console.log('PROOF 3b error.message :', error.message);
    console.log('PROOF 3b error.code    :', (error as any).code ?? 'n/a');
    console.log('PROOF 3b VERDICT       :', error.message?.includes('Only admins can change role')
      ? '✅ PASS — trigger raised "Only admins can change role"'
      : `⚠️  PARTIAL — error returned but message differs: "${error.message}"`
    );
  } else {
    console.log('PROOF 3b rows returned :', data?.length ?? 0);
    console.log('PROOF 3b VERDICT       :', '❌ FAIL — no error raised, role may have changed');
  }
  console.groupEnd();

  return { data, error };
}
